<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { Form } from '@inertiajs/vue3';
import creditCardType from 'credit-card-type';
import { vMaska } from 'maska/vue';
import InputError from '@/components/InputError.vue';
import { Button } from '@/components/ui/button';
import {
    isCardBrandConsistent,
    isValidCardLength,
    isValidCvv,
    isValidExpiration,
    luhnCheck,
} from '@/composables/useCardValidation';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { usePaytraceEncryption } from '@/composables/usePaytraceEncryption';
import { useSignupStorage } from '@/composables/useSignupStorage';
import { store } from '@/routes/customer-signup';

const { signup } = useSignupStorage();
const {
    isReady: isEncryptionReady,
    error: encryptionError,
    encryptValue,
} = usePaytraceEncryption();

const recaptchaToken = ref('');
const recaptchaReady = ref(false);
const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
let recaptchaWidgetId: number | null = null;

function onRecaptchaVerified(token: string): void {
    recaptchaToken.value = token;
}

function onRecaptchaExpired(): void {
    recaptchaToken.value = '';
}

function initRecaptchaWidget(): void {
    recaptchaWidgetId = window.grecaptcha.render('recaptcha-checkbox', {
        sitekey: recaptchaSiteKey,
        callback: onRecaptchaVerified,
        'expired-callback': onRecaptchaExpired,
        'error-callback': onRecaptchaExpired,
    });
    recaptchaReady.value = true;
}

onMounted(() => {
    if (!recaptchaSiteKey) {
        return;
    }

    if (typeof window.grecaptcha !== 'undefined' && window.grecaptcha.render) {
        initRecaptchaWidget();
        return;
    }
    const script = document.createElement('script');
    script.src =
        'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit';
    script.async = true;
    window.onRecaptchaLoad = () => {
        initRecaptchaWidget();
    };
    document.head.appendChild(script);
});

const showFlowerBedsStep1 = ref(false);
const showFlowerBedsLiability = ref(false);
const flowerBedsDamageable = ref('');
const showOvergrownDialog = ref(false);
const showLiabilityDialog = ref(false);
const liabilityDialogItem = ref<'vinyl_fence' | 'pool' | 'trampoline'>(
    'vinyl_fence',
);
const clientErrors = ref<Record<string, string>>({});

const vinylFenceAgree = ref(
    signup.white_vinyl_fence === 'yes' && signup.vinyl_fence_damage === 'no'
        ? 'yes'
        : '',
);
const poolAgree = ref(
    signup.above_ground_pool === 'yes' && signup.pool_damage === 'no'
        ? 'yes'
        : '',
);
const trampolineAgree = ref(
    signup.trampoline === 'yes' && signup.trampoline_damage === 'no'
        ? 'yes'
        : '',
);

const liabilityLabels: Record<string, { item: string; noLabel: string }> = {
    vinyl_fence: { item: 'vinyl plastic fences', noLabel: 'No Fence' },
    pool: { item: 'above ground pools', noLabel: 'No Pool' },
    trampoline: { item: 'trampolines', noLabel: 'No Trampoline' },
};

function openLiabilityDialog(
    item: 'vinyl_fence' | 'pool' | 'trampoline',
): void {
    liabilityDialogItem.value = item;
    showLiabilityDialog.value = true;
}

function handleLiabilityAgree(): void {
    if (liabilityDialogItem.value === 'vinyl_fence') {
        vinylFenceAgree.value = 'yes';
    } else if (liabilityDialogItem.value === 'pool') {
        poolAgree.value = 'yes';
    } else {
        trampolineAgree.value = 'yes';
    }
    showLiabilityDialog.value = false;
}

function handleLiabilityRemoveItem(): void {
    if (liabilityDialogItem.value === 'vinyl_fence') {
        signup.white_vinyl_fence = 'no';
    } else if (liabilityDialogItem.value === 'pool') {
        signup.above_ground_pool = 'no';
    } else {
        signup.trampoline = 'no';
    }
    showLiabilityDialog.value = false;
}

watch(
    () => signup.community_gate,
    (val) => {
        if (val === 'no') {
            signup.gate_code = '';
        }
    },
);

watch(
    () => signup.backyard_gate,
    (val) => {
        if (val === 'no') {
            signup.backyard_gate_locked = 'no';
            signup.backyard_gate_code = '';
        }
    },
);

watch(
    () => signup.backyard_gate_locked,
    (val) => {
        if (val === 'no') {
            signup.backyard_gate_code = '';
        }
    },
);

watch(
    () => signup.flower_beds,
    (val) => {
        if (val === 'yes') {
            showFlowerBedsStep1.value = true;
        } else {
            signup.flower_beds_line = 'no';
            flowerBedsDamageable.value = '';
        }
    },
);

watch(
    () => signup.flower_beds_line,
    (val) => {
        if (val === 'no') {
            flowerBedsDamageable.value = '';
        }
    },
);

watch(flowerBedsDamageable, (val) => {
    if (val === 'yes') {
        showFlowerBedsLiability.value = true;
    }
});

watch(
    () => signup.is_grass_six_inches,
    (val) => {
        if (val === 'no') {
            signup.city_pickup_requirement = '';
            signup.bagged_grass_location = '';
        }
    },
);

watch(
    () => signup.is_grass_overgrown,
    (val) => {
        if (val === 'yes') {
            showOvergrownDialog.value = true;
        }
    },
);

watch(
    () => signup.referral,
    () => {
        signup.referral_2 = '';
    },
);

watch(
    () => signup.pets_in_backyard,
    (val) => {
        if (val === 'no') {
            signup.pets_avoid = 'no';
        }
    },
);

watch(
    () => signup.white_vinyl_fence,
    (val) => {
        if (val === 'no') {
            signup.vinyl_fence_damage = '';
            vinylFenceAgree.value = '';
        }
    },
);

watch(
    () => signup.above_ground_pool,
    (val) => {
        if (val === 'no') {
            signup.pool_damage = '';
            poolAgree.value = '';
        }
    },
);

watch(
    () => signup.trampoline,
    (val) => {
        if (val === 'no') {
            signup.trampoline_damage = '';
            trampolineAgree.value = '';
        }
    },
);

watch(vinylFenceAgree, (val) => {
    if (val === 'yes') {
        signup.vinyl_fence_damage = 'no';
    } else if (val === 'no') {
        signup.vinyl_fence_damage = '';
        openLiabilityDialog('vinyl_fence');
    }
});

watch(poolAgree, (val) => {
    if (val === 'yes') {
        signup.pool_damage = 'no';
    } else if (val === 'no') {
        signup.pool_damage = '';
        openLiabilityDialog('pool');
    }
});

watch(trampolineAgree, (val) => {
    if (val === 'yes') {
        signup.trampoline_damage = 'no';
    } else if (val === 'no') {
        signup.trampoline_damage = '';
        openLiabilityDialog('trampoline');
    }
});

defineEmits<{
    (e: 'back'): void;
}>();

const optionalServicesJson = computed(() => {
    const services = [...signup.optional_services];
    if (signup.corner_lot === 'yes') {
        services.push('corner_lot');
    }
    return JSON.stringify(services);
});

const cardBrand = ref('');

const cardType = computed(() => {
    const map: Record<string, string> = {
        visa: 'visa',
        mastercard: 'mc',
        discover: 'disc',
        'american-express': 'amex',
    };
    return map[cardBrand.value] ?? '';
});

const cardNumberMask = computed(() => {
    return cardBrand.value === 'american-express'
        ? '#### ###### #####'
        : '#### #### #### ####';
});

const cvvMask = computed(() => {
    return cardBrand.value === 'american-express' ? '####' : '###';
});

const cvvLabel = computed(() => {
    return cardBrand.value === 'american-express' ? 'CID' : 'CVV';
});

function onCardNumberMaska(detail: { masked: string; unmasked: string }): void {
    signup.card_number = detail.unmasked;
    const types = creditCardType(detail.unmasked);
    cardBrand.value = types.length === 1 ? types[0].type : '';
}

function onCardExpirationMaska(detail: {
    masked: string;
    unmasked: string;
}): void {
    signup.card_expiration = detail.masked;
}

function onCardCvvMaska(detail: { masked: string; unmasked: string }): void {
    signup.card_cvv = detail.unmasked;
}

const inputClasses =
    'file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground border-input h-12 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-mm-green focus-visible:ring-mm-green/50 focus-visible:ring-[3px] md:text-sm';

const selectClasses =
    'border-input h-12 w-full rounded-md border bg-transparent px-3 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-mm-green focus-visible:ring-mm-green/50 focus-visible:ring-[3px] md:text-sm appearance-none bg-[url("data:image/svg+xml,%3Csvg%20xmlns%3D%27http%3A//www.w3.org/2000/svg%27%20width%3D%2716%27%20height%3D%2716%27%20viewBox%3D%270%200%2024%2024%27%20fill%3D%27none%27%20stroke%3D%27%23666%27%20stroke-width%3D%272%27%20stroke-linecap%3D%27round%27%20stroke-linejoin%3D%27round%27%3E%3Cpolyline%20points%3D%276%209%2012%2015%2018%209%27%3E%3C/polyline%3E%3C/svg%3E")] bg-[length:16px] bg-[right_12px_center] bg-no-repeat pr-10';

const textareaClasses =
    'w-full border border-input rounded-md px-3 py-3 text-base shadow-xs placeholder:text-muted-foreground focus-visible:border-mm-green focus-visible:ring-mm-green/50 focus-visible:ring-[3px] outline-none transition-[color,box-shadow] md:text-sm';

function formatDigits(digits: string): string {
    if (digits.length === 0) {
        return '';
    }
    if (digits.length <= 3) {
        return `(${digits}`;
    }
    if (digits.length <= 6) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function onBagTypeChange(): void {
    if (signup.city_pickup_requirement === 'No Bag') {
        signup.bagged_grass_location = '';
    }
}

function onPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const cursorPos = input.selectionStart ?? 0;
    const digitsBeforeCursor = input.value
        .slice(0, cursorPos)
        .replace(/\D/g, '').length;
    const digits = input.value.replace(/\D/g, '').slice(0, 10);
    const formatted = formatDigits(digits);

    input.value = formatted;
    signup.phone = formatted;

    let digitsSeen = 0;
    let newPos = formatted.length;
    for (let i = 0; i < formatted.length; i++) {
        if (/\d/.test(formatted[i])) {
            digitsSeen++;
        }
        if (digitsSeen === digitsBeforeCursor) {
            newPos = i + 1;
            break;
        }
    }
    if (digitsBeforeCursor === 0) {
        newPos = formatted.length > 0 ? 1 : 0;
    }
    input.setSelectionRange(newPos, newPos);
}

function handleFlowerBedsStep1Yes(): void {
    signup.flower_beds_line = 'yes';
    showFlowerBedsStep1.value = false;
}

function handleFlowerBedsStep1No(): void {
    signup.flower_beds_line = 'no';
    showFlowerBedsStep1.value = false;
}

function handleFlowerBedsLiabilityAgree(): void {
    showFlowerBedsLiability.value = false;
}

function handleFlowerBedsLiabilityRemove(): void {
    signup.flower_beds = 'no';
    signup.flower_beds_line = 'no';
    flowerBedsDamageable.value = '';
    showFlowerBedsLiability.value = false;
}

function validate(): boolean {
    const errors: Record<string, string> = {};

    if (signup.community_gate === 'yes' && !signup.gate_code) {
        errors.gate_code =
            'The gate code is required when you have a community gate.';
    }

    if (signup.backyard_gate === 'yes' && !signup.backyard_gate_locked) {
        errors.backyard_gate_locked =
            'Please indicate if the backyard gate is locked.';
    }

    if (
        signup.backyard_gate === 'yes' &&
        signup.backyard_gate_locked === 'yes' &&
        !signup.backyard_gate_code
    ) {
        errors.backyard_gate_code =
            'The gate code is required when the backyard gate is locked.';
    }

    if (signup.flower_beds === 'yes' && !signup.flower_beds_line) {
        errors.flower_beds_line =
            'Please indicate if you want the beds line trimmed.';
    }

    if (
        signup.flower_beds === 'yes' &&
        signup.flower_beds_line === 'yes' &&
        !flowerBedsDamageable.value
    ) {
        errors.flower_beds_damageable =
            'Please indicate if there is anything that could be damaged.';
    }

    if (signup.pets_in_backyard === 'yes' && !signup.pets_avoid) {
        errors.pets_avoid = 'Please answer the pets question.';
    }

    if (signup.white_vinyl_fence === 'yes' && vinylFenceAgree.value !== 'yes') {
        errors.vinyl_fence_damage =
            'You must agree to the liability release for vinyl fences.';
    }

    if (signup.above_ground_pool === 'yes' && poolAgree.value !== 'yes') {
        errors.pool_damage =
            'You must agree to the liability release for above ground pools.';
    }

    if (signup.trampoline === 'yes' && trampolineAgree.value !== 'yes') {
        errors.trampoline_damage =
            'You must agree to the liability release for trampolines.';
    }

    const expirationResult = isValidExpiration(signup.card_expiration);
    if (!expirationResult.valid) {
        errors.card_expiration = expirationResult.reason!;
    }

    const cardDigits = signup.card_number.replace(/\D/g, '');
    if (cardDigits.length > 0) {
        if (!isValidCardLength(signup.card_number)) {
            errors.card_number = 'Please enter a valid card number.';
        } else if (!luhnCheck(signup.card_number)) {
            errors.card_number = 'The card number is not valid.';
        } else if (!isCardBrandConsistent(cardBrand.value, cardType.value)) {
            errors.card_number =
                'The card number does not match the detected card type.';
        }
    }

    const cvvResult = isValidCvv(signup.card_cvv, cardBrand.value);
    if (!cvvResult.valid) {
        errors.card_cvv = cvvResult.reason!;
    }

    if (!isEncryptionReady.value) {
        errors.card_number =
            encryptionError.value ??
            'Payment encryption is loading. Please wait a moment and try again.';
    }

    if (recaptchaSiteKey && !recaptchaReady.value) {
        errors.recaptcha_token =
            'Human verification is loading. Please wait a moment and try again.';
    } else if (recaptchaSiteKey && !recaptchaToken.value) {
        errors.recaptcha_token =
            'Please complete the human verification checkbox.';
    }

    clientErrors.value = errors;

    if (Object.keys(errors).length > 0) {
        const firstErrorKey = Object.keys(errors)[0];
        const el =
            document.getElementById(firstErrorKey) ??
            document.querySelector(`[name="${firstErrorKey}"]`) ??
            document.querySelector(`[name="${firstErrorKey}_radio"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
    }

    return true;
}

function validateAndSubmit(visit: { data: Record<string, unknown> }): boolean {
    if (!validate()) {
        return false;
    }

    visit.data.card_number = encryptValue(signup.card_number);

    if (recaptchaSiteKey) {
        visit.data.recaptcha_token = recaptchaToken.value;
    }

    return true;
}
</script>

<template>
    <Form
        v-bind="store.form()"
        v-slot="{ errors, processing }"
        class="space-y-8"
        :on-before="validateAndSubmit"
    >
        <div
            v-if="errors.signup"
            class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
        >
            {{ errors.signup }}
        </div>

        <input type="hidden" name="service_type" value="ongoing" />
        <input type="hidden" name="occupancy" value="occupied" />
        <input type="hidden" name="frequency" :value="signup.frequency" />
        <input type="hidden" name="mow_areas" :value="signup.mow_areas" />
        <input type="hidden" name="start_date" :value="signup.start_date" />
        <input
            type="hidden"
            name="optional_services"
            :value="optionalServicesJson"
        />
        <input type="hidden" name="address" :value="signup.address" />
        <input type="hidden" name="address_1" :value="signup.address_1" />
        <input type="hidden" name="city" :value="signup.city" />
        <input type="hidden" name="state" :value="signup.state" />
        <input type="hidden" name="zip" :value="signup.zip" />
        <input
            type="hidden"
            name="melissa"
            :value="JSON.stringify(signup.melissa)"
        />
        <input
            type="hidden"
            name="address_check"
            :value="JSON.stringify(signup.address_check)"
        />
        <input type="hidden" name="price" :value="signup.price" />
        <input type="hidden" name="tax" :value="signup.tax" />
        <input type="hidden" name="tax_rate" :value="signup.tax_rate" />
        <input type="hidden" name="cost_service" :value="signup.cost_service" />
        <input
            type="hidden"
            name="calculated_tax"
            :value="signup.calculated_tax"
        />
        <input
            type="hidden"
            name="subtotal_mowing"
            :value="signup.subtotal_mowing"
        />
        <input type="hidden" name="total_mowing" :value="signup.total_mowing" />
        <input type="hidden" name="lawn_sqft" :value="signup.lawn_sqft" />
        <input type="hidden" name="gate_code" :value="signup.gate_code" />
        <input
            type="hidden"
            name="backyard_gate_locked"
            :value="signup.backyard_gate_locked"
        />
        <input
            type="hidden"
            name="backyard_gate_code"
            :value="signup.backyard_gate_code"
        />
        <input
            type="hidden"
            name="flower_beds_line"
            :value="signup.flower_beds_line"
        />
        <input
            type="hidden"
            name="flower_beds_damage"
            :value="flowerBedsDamageable === 'yes' ? 'yes' : 'no'"
        />
        <input type="hidden" name="pets_avoid" :value="signup.pets_avoid" />
        <input
            type="hidden"
            name="vinyl_fence_damage"
            :value="signup.vinyl_fence_damage"
        />
        <input type="hidden" name="pool_damage" :value="signup.pool_damage" />
        <input
            type="hidden"
            name="trampoline_damage"
            :value="signup.trampoline_damage"
        />

        <!-- Account Details -->
        <h2 class="text-xl font-bold text-mm-dark">Account Details</h2>

        <div class="space-y-6">
            <!-- Name -->
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div class="space-y-2">
                    <Label for="first_name"
                        >First name <span class="text-mm-green">*</span></Label
                    >
                    <input
                        id="first_name"
                        name="first_name"
                        v-model="signup.first_name"
                        type="text"
                        required
                        autofocus
                        placeholder="First name"
                        :class="inputClasses"
                    />
                    <InputError :message="errors.first_name" />
                </div>
                <div class="space-y-2">
                    <Label for="last_name"
                        >Last name <span class="text-mm-green">*</span></Label
                    >
                    <input
                        id="last_name"
                        name="last_name"
                        v-model="signup.last_name"
                        type="text"
                        required
                        placeholder="Last name"
                        :class="inputClasses"
                    />
                    <InputError :message="errors.last_name" />
                </div>
            </div>

            <!-- Email -->
            <div class="space-y-2">
                <Label for="email"
                    >Email <span class="text-mm-green">*</span></Label
                >
                <input
                    id="email"
                    name="email"
                    v-model="signup.email"
                    type="email"
                    required
                    placeholder="you@company.com"
                    :class="inputClasses"
                />
                <InputError :message="errors.email" />
            </div>

            <!-- Phone -->
            <div class="space-y-2">
                <Label for="phone"
                    >Phone number <span class="text-mm-green">*</span></Label
                >
                <input
                    id="phone"
                    name="phone"
                    :value="signup.phone"
                    type="tel"
                    required
                    maxlength="14"
                    placeholder="(555) 000-0000"
                    :class="inputClasses"
                    @input="onPhoneInput"
                />
                <InputError :message="errors.phone" />
            </div>

            <!-- Is it a cellphone? -->
            <div class="space-y-2">
                <Label for="is_cellphone"
                    >Is it a cellphone?
                    <span class="text-mm-green">*</span></Label
                >
                <select
                    id="is_cellphone"
                    name="is_cellphone"
                    v-model="signup.is_cellphone"
                    required
                    :class="selectClasses"
                >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                </select>
                <InputError :message="errors.is_cellphone" />
            </div>

            <!-- Grass 6 inches or taller? -->
            <div class="space-y-2">
                <Label for="is_grass_six_inches" class="leading-relaxed">
                    Is your grass 6 inches or taller?
                    <span class="text-mm-green">*</span>
                </Label>
                <select
                    id="is_grass_six_inches"
                    name="is_grass_six_inches"
                    v-model="signup.is_grass_six_inches"
                    required
                    :class="selectClasses"
                >
                    <option value="" disabled>Select an option</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                </select>
                <InputError :message="errors.is_grass_six_inches" />
            </div>

            <!-- Bag type -->
            <div v-if="signup.is_grass_six_inches === 'yes'" class="space-y-2">
                <Label for="city_pickup_requirement" class="leading-relaxed">
                    Mow Managers bags grass 6 inches or taller and we charge
                    double for the first mow. What type of bag would you like
                    us to use?
                    <span class="text-mm-green">*</span>
                </Label>
                <select
                    id="city_pickup_requirement"
                    name="city_pickup_requirement"
                    v-model="signup.city_pickup_requirement"
                    required
                    :class="selectClasses"
                    @change="onBagTypeChange"
                >
                    <option value="" disabled>Select an option</option>
                    <option value="Paper">Paper</option>
                    <option value="Plastic">Plastic</option>
                    <option value="No Bag">No Bag</option>
                </select>
                <p class="text-xs text-gray-500 italic">
                    Will not bag yards over 8,000 sq ft of turf.
                </p>
                <InputError :message="errors.city_pickup_requirement" />
            </div>

            <!-- Bagged grass placement -->
            <div
                v-if="
                    signup.city_pickup_requirement === 'Paper' ||
                    signup.city_pickup_requirement === 'Plastic'
                "
                class="space-y-2"
            >
                <Label for="bagged_grass_location" class="leading-relaxed">
                    Using your selected bag type. Where would you like us to
                    leave the bagged grass clippings?
                    <span class="text-mm-green">*</span>
                </Label>
                <select
                    id="bagged_grass_location"
                    name="bagged_grass_location"
                    v-model="signup.bagged_grass_location"
                    required
                    :class="selectClasses"
                >
                    <option value="" disabled>Select an option</option>
                    <option value="In trash can">In trash can</option>
                    <option value="In back alley">In back alley</option>
                    <option value="On front curb">On front curb</option>
                </select>
                <InputError :message="errors.bagged_grass_location" />
            </div>

            <!-- Is your grass 12 inches or taller? -->
            <div class="space-y-2">
                <Label
                    >Is your grass 12 inches or taller?
                    <span class="text-mm-green">*</span></Label
                >
                <div class="space-y-1">
                    <label class="flex cursor-pointer items-center gap-3">
                        <input
                            type="radio"
                            name="is_grass_overgrown"
                            value="yes"
                            v-model="signup.is_grass_overgrown"
                            class="peer sr-only"
                        />
                        <span
                            class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
                        >
                            <svg
                                v-if="signup.is_grass_overgrown === 'yes'"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="3"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="size-3 text-white"
                            >
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </span>
                        <span class="text-sm">Yes</span>
                    </label>
                    <label class="flex cursor-pointer items-center gap-3">
                        <input
                            type="radio"
                            name="is_grass_overgrown"
                            value="no"
                            v-model="signup.is_grass_overgrown"
                            class="peer sr-only"
                        />
                        <span
                            class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
                        >
                            <svg
                                v-if="signup.is_grass_overgrown === 'no'"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="3"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="size-3 text-white"
                            >
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </span>
                        <span class="text-sm">No</span>
                    </label>
                </div>
                <InputError :message="errors.is_grass_overgrown" />
            </div>

            <!-- Community gate? -->
            <div class="space-y-2">
                <Label
                    >Community gate? <span class="text-mm-green">*</span></Label
                >
                <div class="space-y-1">
                    <label class="flex cursor-pointer items-center gap-3">
                        <input
                            type="radio"
                            name="community_gate"
                            value="yes"
                            v-model="signup.community_gate"
                            class="peer sr-only"
                        />
                        <span
                            class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
                        >
                            <svg
                                v-if="signup.community_gate === 'yes'"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="3"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="size-3 text-white"
                            >
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </span>
                        <span class="text-sm">Yes</span>
                    </label>
                    <label class="flex cursor-pointer items-center gap-3">
                        <input
                            type="radio"
                            name="community_gate"
                            value="no"
                            v-model="signup.community_gate"
                            class="peer sr-only"
                        />
                        <span
                            class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
                        >
                            <svg
                                v-if="signup.community_gate === 'no'"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="3"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="size-3 text-white"
                            >
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </span>
                        <span class="text-sm">No</span>
                    </label>
                </div>
                <InputError :message="errors.community_gate" />

                <div
                    v-if="signup.community_gate === 'yes'"
                    class="mt-3 space-y-2 border-l-2 border-mm-green/30 pl-6"
                >
                    <Label for="gate_code"
                        >Gate code <span class="text-mm-green">*</span></Label
                    >
                    <input
                        id="gate_code"
                        v-model="signup.gate_code"
                        type="text"
                        required
                        placeholder="Enter gate code"
                        :class="inputClasses"
                    />
                    <InputError
                        :message="errors.gate_code || clientErrors.gate_code"
                    />
                </div>
            </div>

            <!-- Backyard gate? -->
            <div class="space-y-2">
                <Label
                    >Backyard gate? <span class="text-mm-green">*</span></Label
                >
                <div class="space-y-1">
                    <label class="flex cursor-pointer items-center gap-3">
                        <input
                            type="radio"
                            name="backyard_gate"
                            value="yes"
                            v-model="signup.backyard_gate"
                            class="peer sr-only"
                        />
                        <span
                            class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
                        >
                            <svg
                                v-if="signup.backyard_gate === 'yes'"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="3"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="size-3 text-white"
                            >
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </span>
                        <span class="text-sm">Yes</span>
                    </label>
                    <label class="flex cursor-pointer items-center gap-3">
                        <input
                            type="radio"
                            name="backyard_gate"
                            value="no"
                            v-model="signup.backyard_gate"
                            class="peer sr-only"
                        />
                        <span
                            class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
                        >
                            <svg
                                v-if="signup.backyard_gate === 'no'"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="3"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="size-3 text-white"
                            >
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </span>
                        <span class="text-sm">No</span>
                    </label>
                </div>
                <InputError :message="errors.backyard_gate" />

                <div
                    v-if="signup.backyard_gate === 'yes'"
                    class="mt-3 space-y-3 border-l-2 border-mm-green/30 pl-6"
                >
                    <div class="space-y-2">
                        <Label>Is it locked?</Label>
                        <div class="space-y-1">
                            <label
                                class="flex cursor-pointer items-center gap-3"
                            >
                                <input
                                    type="radio"
                                    name="backyard_gate_locked_radio"
                                    value="yes"
                                    v-model="signup.backyard_gate_locked"
                                    class="peer sr-only"
                                />
                                <span
                                    class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
                                >
                                    <svg
                                        v-if="
                                            signup.backyard_gate_locked ===
                                            'yes'
                                        "
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="3"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        class="size-3 text-white"
                                    >
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                </span>
                                <span class="text-sm">Yes</span>
                            </label>
                            <label
                                class="flex cursor-pointer items-center gap-3"
                            >
                                <input
                                    type="radio"
                                    name="backyard_gate_locked_radio"
                                    value="no"
                                    v-model="signup.backyard_gate_locked"
                                    class="peer sr-only"
                                />
                                <span
                                    class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
                                >
                                    <svg
                                        v-if="
                                            signup.backyard_gate_locked === 'no'
                                        "
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="3"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        class="size-3 text-white"
                                    >
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                </span>
                                <span class="text-sm">No</span>
                            </label>
                        </div>
                        <InputError
                            :message="clientErrors.backyard_gate_locked"
                        />
                    </div>

                    <div
                        v-if="signup.backyard_gate_locked === 'yes'"
                        class="space-y-2"
                    >
                        <Label for="backyard_gate_code"
                            >Gate code
                            <span class="text-mm-green">*</span></Label
                        >
                        <input
                            id="backyard_gate_code"
                            v-model="signup.backyard_gate_code"
                            type="text"
                            required
                            placeholder="Enter gate code"
                            :class="inputClasses"
                        />
                        <InputError
                            :message="
                                errors.backyard_gate_code ||
                                clientErrors.backyard_gate_code
                            "
                        />
                    </div>
                </div>
            </div>

            <!-- Flower beds? -->
            <div class="space-y-2">
                <Label>Flower beds? <span class="text-mm-green">*</span></Label>
                <div class="space-y-1">
                    <label class="flex cursor-pointer items-center gap-3">
                        <input
                            type="radio"
                            name="flower_beds"
                            value="yes"
                            v-model="signup.flower_beds"
                            class="peer sr-only"
                        />
                        <span
                            class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
                        >
                            <svg
                                v-if="signup.flower_beds === 'yes'"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="3"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="size-3 text-white"
                            >
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </span>
                        <span class="text-sm">Yes</span>
                    </label>
                    <label class="flex cursor-pointer items-center gap-3">
                        <input
                            type="radio"
                            name="flower_beds"
                            value="no"
                            v-model="signup.flower_beds"
                            class="peer sr-only"
                        />
                        <span
                            class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
                        >
                            <svg
                                v-if="signup.flower_beds === 'no'"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="3"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="size-3 text-white"
                            >
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </span>
                        <span class="text-sm">No</span>
                    </label>
                </div>
                <InputError :message="errors.flower_beds" />

                <div
                    v-if="signup.flower_beds === 'yes'"
                    class="mt-3 space-y-3 border-l-2 border-mm-green/30 pl-6"
                >
                    <div class="space-y-2">
                        <Label>Line trim the beds?</Label>
                        <div class="space-y-1">
                            <label
                                class="flex cursor-pointer items-center gap-3"
                            >
                                <input
                                    type="radio"
                                    name="flower_beds_line_radio"
                                    value="yes"
                                    v-model="signup.flower_beds_line"
                                    class="peer sr-only"
                                />
                                <span
                                    class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
                                >
                                    <svg
                                        v-if="signup.flower_beds_line === 'yes'"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="3"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        class="size-3 text-white"
                                    >
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                </span>
                                <span class="text-sm">Yes</span>
                            </label>
                            <label
                                class="flex cursor-pointer items-center gap-3"
                            >
                                <input
                                    type="radio"
                                    name="flower_beds_line_radio"
                                    value="no"
                                    v-model="signup.flower_beds_line"
                                    class="peer sr-only"
                                />
                                <span
                                    class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
                                >
                                    <svg
                                        v-if="signup.flower_beds_line === 'no'"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="3"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        class="size-3 text-white"
                                    >
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                </span>
                                <span class="text-sm">No</span>
                            </label>
                        </div>
                        <InputError :message="clientErrors.flower_beds_line" />
                    </div>

                    <div
                        v-if="signup.flower_beds_line === 'yes'"
                        class="space-y-2"
                    >
                        <Label class="leading-relaxed"
                            >Is there anything in the flower beds that could be
                            damaged?</Label
                        >
                        <div class="space-y-1">
                            <label
                                class="flex cursor-pointer items-center gap-3"
                            >
                                <input
                                    type="radio"
                                    name="flower_beds_damageable_radio"
                                    value="yes"
                                    v-model="flowerBedsDamageable"
                                    class="peer sr-only"
                                />
                                <span
                                    class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
                                >
                                    <svg
                                        v-if="flowerBedsDamageable === 'yes'"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="3"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        class="size-3 text-white"
                                    >
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                </span>
                                <span class="text-sm">Yes</span>
                            </label>
                            <label
                                class="flex cursor-pointer items-center gap-3"
                            >
                                <input
                                    type="radio"
                                    name="flower_beds_damageable_radio"
                                    value="no"
                                    v-model="flowerBedsDamageable"
                                    class="peer sr-only"
                                />
                                <span
                                    class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
                                >
                                    <svg
                                        v-if="flowerBedsDamageable === 'no'"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="3"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        class="size-3 text-white"
                                    >
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                </span>
                                <span class="text-sm">No</span>
                            </label>
                        </div>
                        <InputError
                            :message="clientErrors.flower_beds_damageable"
                        />
                    </div>
                </div>
            </div>

            <!-- Pets in backyard? -->
            <div class="space-y-2">
                <Label
                    >Pets in backyard?
                    <span class="text-mm-green">*</span></Label
                >
                <div class="space-y-1">
                    <label class="flex cursor-pointer items-center gap-3">
                        <input
                            type="radio"
                            name="pets_in_backyard"
                            value="yes"
                            v-model="signup.pets_in_backyard"
                            class="peer sr-only"
                        />
                        <span
                            class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
                        >
                            <svg
                                v-if="signup.pets_in_backyard === 'yes'"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="3"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="size-3 text-white"
                            >
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </span>
                        <span class="text-sm">Yes</span>
                    </label>
                    <label class="flex cursor-pointer items-center gap-3">
                        <input
                            type="radio"
                            name="pets_in_backyard"
                            value="no"
                            v-model="signup.pets_in_backyard"
                            class="peer sr-only"
                        />
                        <span
                            class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
                        >
                            <svg
                                v-if="signup.pets_in_backyard === 'no'"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="3"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="size-3 text-white"
                            >
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </span>
                        <span class="text-sm">No</span>
                    </label>
                </div>
                <InputError :message="errors.pets_in_backyard" />

                <div
                    v-if="signup.pets_in_backyard === 'yes'"
                    class="mt-3 space-y-2 border-l-2 border-mm-green/30 pl-6"
                >
                    <Label class="leading-relaxed"
                        >Can we mow around them without fear of being bitten or
                        creating problems for our crew?</Label
                    >
                    <div class="space-y-1">
                        <label class="flex cursor-pointer items-center gap-3">
                            <input
                                type="radio"
                                name="pets_avoid_radio"
                                value="no"
                                v-model="signup.pets_avoid"
                                class="peer sr-only"
                            />
                            <span
                                class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
                            >
                                <svg
                                    v-if="signup.pets_avoid === 'no'"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="3"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="size-3 text-white"
                                >
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            </span>
                            <span class="text-sm">Yes</span>
                        </label>
                        <label class="flex cursor-pointer items-center gap-3">
                            <input
                                type="radio"
                                name="pets_avoid_radio"
                                value="yes"
                                v-model="signup.pets_avoid"
                                class="peer sr-only"
                            />
                            <span
                                class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
                            >
                                <svg
                                    v-if="signup.pets_avoid === 'yes'"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="3"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="size-3 text-white"
                                >
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            </span>
                            <span class="text-sm"
                                >Yes, but please knock on the front door before
                                entering backyard</span
                            >
                        </label>
                    </div>
                    <InputError :message="clientErrors.pets_avoid" />
                </div>
            </div>

            <!-- White vinyl fence? -->
            <div class="space-y-2">
                <Label
                    >Is your fence made of white vinyl plastic?
                    <span class="text-mm-green">*</span></Label
                >
                <div class="space-y-1">
                    <label class="flex cursor-pointer items-center gap-3">
                        <input
                            type="radio"
                            name="white_vinyl_fence"
                            value="yes"
                            v-model="signup.white_vinyl_fence"
                            class="peer sr-only"
                        />
                        <span
                            class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
                        >
                            <svg
                                v-if="signup.white_vinyl_fence === 'yes'"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="3"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="size-3 text-white"
                            >
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </span>
                        <span class="text-sm">Yes</span>
                    </label>
                    <label class="flex cursor-pointer items-center gap-3">
                        <input
                            type="radio"
                            name="white_vinyl_fence"
                            value="no"
                            v-model="signup.white_vinyl_fence"
                            class="peer sr-only"
                        />
                        <span
                            class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
                        >
                            <svg
                                v-if="signup.white_vinyl_fence === 'no'"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="3"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="size-3 text-white"
                            >
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </span>
                        <span class="text-sm">No</span>
                    </label>
                </div>
                <InputError :message="errors.white_vinyl_fence" />
                <InputError
                    :message="
                        errors.vinyl_fence_damage ||
                        clientErrors.vinyl_fence_damage
                    "
                />

                <div
                    v-if="signup.white_vinyl_fence === 'yes'"
                    class="mt-3 space-y-2 border-l-2 border-mm-green/30 pl-6"
                >
                    <Label class="leading-relaxed"
                        >Vinyl plastic fences are a challenge for us and all
                        lawn companies, so we ask to be released from liability
                        if anything happens to this item. Do you agree?</Label
                    >
                    <div class="space-y-1">
                        <label class="flex cursor-pointer items-center gap-3">
                            <input
                                type="radio"
                                name="vinyl_fence_agree"
                                value="yes"
                                v-model="vinylFenceAgree"
                                class="peer sr-only"
                            />
                            <span
                                class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
                            >
                                <svg
                                    v-if="vinylFenceAgree === 'yes'"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="3"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="size-3 text-white"
                                >
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            </span>
                            <span class="text-sm">Yes</span>
                        </label>
                        <label class="flex cursor-pointer items-center gap-3">
                            <input
                                type="radio"
                                name="vinyl_fence_agree"
                                value="no"
                                v-model="vinylFenceAgree"
                                class="peer sr-only"
                            />
                            <span
                                class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs"
                            />
                            <span class="text-sm">No</span>
                        </label>
                    </div>
                </div>
            </div>

            <!-- Above ground pool? -->
            <div class="space-y-2">
                <Label
                    >Do you have an above ground pool?
                    <span class="text-mm-green">*</span></Label
                >
                <div class="space-y-1">
                    <label class="flex cursor-pointer items-center gap-3">
                        <input
                            type="radio"
                            name="above_ground_pool"
                            value="yes"
                            v-model="signup.above_ground_pool"
                            class="peer sr-only"
                        />
                        <span
                            class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
                        >
                            <svg
                                v-if="signup.above_ground_pool === 'yes'"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="3"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="size-3 text-white"
                            >
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </span>
                        <span class="text-sm">Yes</span>
                    </label>
                    <label class="flex cursor-pointer items-center gap-3">
                        <input
                            type="radio"
                            name="above_ground_pool"
                            value="no"
                            v-model="signup.above_ground_pool"
                            class="peer sr-only"
                        />
                        <span
                            class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
                        >
                            <svg
                                v-if="signup.above_ground_pool === 'no'"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="3"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="size-3 text-white"
                            >
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </span>
                        <span class="text-sm">No</span>
                    </label>
                </div>
                <InputError :message="errors.above_ground_pool" />
                <InputError
                    :message="errors.pool_damage || clientErrors.pool_damage"
                />

                <div
                    v-if="signup.above_ground_pool === 'yes'"
                    class="mt-3 space-y-2 border-l-2 border-mm-green/30 pl-6"
                >
                    <Label class="leading-relaxed"
                        >Above ground pools are a challenge for us and all lawn
                        companies, so we ask to be released from liability if
                        anything happens to this item. Do you agree?</Label
                    >
                    <div class="space-y-1">
                        <label class="flex cursor-pointer items-center gap-3">
                            <input
                                type="radio"
                                name="pool_agree"
                                value="yes"
                                v-model="poolAgree"
                                class="peer sr-only"
                            />
                            <span
                                class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
                            >
                                <svg
                                    v-if="poolAgree === 'yes'"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="3"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="size-3 text-white"
                                >
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            </span>
                            <span class="text-sm">Yes</span>
                        </label>
                        <label class="flex cursor-pointer items-center gap-3">
                            <input
                                type="radio"
                                name="pool_agree"
                                value="no"
                                v-model="poolAgree"
                                class="peer sr-only"
                            />
                            <span
                                class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs"
                            />
                            <span class="text-sm">No</span>
                        </label>
                    </div>
                </div>
            </div>

            <!-- Trampoline? -->
            <div class="space-y-2">
                <Label
                    >Do you have a trampoline and want us to move it?
                    <span class="text-mm-green">*</span></Label
                >
                <div class="space-y-1">
                    <label class="flex cursor-pointer items-center gap-3">
                        <input
                            type="radio"
                            name="trampoline"
                            value="yes"
                            v-model="signup.trampoline"
                            class="peer sr-only"
                        />
                        <span
                            class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
                        >
                            <svg
                                v-if="signup.trampoline === 'yes'"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="3"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="size-3 text-white"
                            >
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </span>
                        <span class="text-sm">Yes</span>
                    </label>
                    <label class="flex cursor-pointer items-center gap-3">
                        <input
                            type="radio"
                            name="trampoline"
                            value="no"
                            v-model="signup.trampoline"
                            class="peer sr-only"
                        />
                        <span
                            class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
                        >
                            <svg
                                v-if="signup.trampoline === 'no'"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="3"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="size-3 text-white"
                            >
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </span>
                        <span class="text-sm">No</span>
                    </label>
                </div>
                <InputError :message="errors.trampoline" />
                <InputError
                    :message="
                        errors.trampoline_damage ||
                        clientErrors.trampoline_damage
                    "
                />

                <div
                    v-if="signup.trampoline === 'yes'"
                    class="mt-3 space-y-2 border-l-2 border-mm-green/30 pl-6"
                >
                    <Label class="leading-relaxed"
                        >Trampolines are a challenge for us and all lawn
                        companies, so we ask to be released from liability if
                        anything happens to this item. Do you agree?</Label
                    >
                    <div class="space-y-1">
                        <label class="flex cursor-pointer items-center gap-3">
                            <input
                                type="radio"
                                name="trampoline_agree"
                                value="yes"
                                v-model="trampolineAgree"
                                class="peer sr-only"
                            />
                            <span
                                class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
                            >
                                <svg
                                    v-if="trampolineAgree === 'yes'"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="3"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="size-3 text-white"
                                >
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            </span>
                            <span class="text-sm">Yes</span>
                        </label>
                        <label class="flex cursor-pointer items-center gap-3">
                            <input
                                type="radio"
                                name="trampoline_agree"
                                value="no"
                                v-model="trampolineAgree"
                                class="peer sr-only"
                            />
                            <span
                                class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs"
                            />
                            <span class="text-sm">No</span>
                        </label>
                    </div>
                </div>
            </div>

            <!-- Flower beds step 1: Line trim the beds? -->
            <Dialog v-model:open="showFlowerBedsStep1">
                <DialogContent class="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Flower Bed Line Trimming</DialogTitle>
                        <DialogDescription>
                            Line trimming around the flowerbed border is
                            included in your service but as an additional option
                            we can use a line trimmer INSIDE your flower beds,
                            if grass &amp; weeds are present. If you choose the
                            option to line trim INSIDE your flower beds, please
                            be aware that shrubs and/or flowers could be damaged
                            &amp; Mow Managers assumes no Liability.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter class="flex gap-2 sm:justify-end">
                        <Button
                            type="button"
                            variant="outline"
                            @click="handleFlowerBedsStep1No"
                        >
                            No
                        </Button>
                        <Button
                            type="button"
                            class="bg-mm-green text-white hover:bg-mm-green-dark"
                            @click="handleFlowerBedsStep1Yes"
                        >
                            Yes
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <!-- Flower beds liability dialog -->
            <Dialog v-model:open="showFlowerBedsLiability">
                <DialogContent class="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Liability Notice</DialogTitle>
                        <DialogDescription>
                            Please be aware we accept no liability when running
                            a line trimmer inside flower beds.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter class="flex gap-2 sm:justify-end">
                        <Button
                            type="button"
                            variant="outline"
                            @click="handleFlowerBedsLiabilityRemove"
                        >
                            No Flower Beds
                        </Button>
                        <Button
                            type="button"
                            class="bg-mm-green text-white hover:bg-mm-green-dark"
                            @click="handleFlowerBedsLiabilityAgree"
                        >
                            I Agree
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <!-- Overgrown grass dialog -->
            <Dialog v-model:open="showOvergrownDialog">
                <DialogContent class="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Overgrown Grass Notice</DialogTitle>
                        <DialogDescription>
                            For grass 12 inches or taller, we will have to use our Bed and Bush crews and there will be an additional charge for the initial service.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter class="flex sm:justify-end">
                        <Button
                            type="button"
                            class="bg-mm-green text-white hover:bg-mm-green-dark"
                            @click="showOvergrownDialog = false"
                        >
                            Okay
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <!-- Shared liability dialog -->
            <Dialog v-model:open="showLiabilityDialog">
                <DialogContent class="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Liability Release Required</DialogTitle>
                        <DialogDescription>
                            We cannot service your property unless you release
                            us from liability for this item. Please go back and
                            change your answer, or agree to release us from
                            liability.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter class="flex gap-2 sm:justify-end">
                        <Button
                            type="button"
                            variant="outline"
                            @click="handleLiabilityRemoveItem"
                        >
                            {{ liabilityLabels[liabilityDialogItem].noLabel }}
                        </Button>
                        <Button
                            type="button"
                            class="bg-mm-green text-white hover:bg-mm-green-dark"
                            @click="handleLiabilityAgree"
                        >
                            I Agree
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <!-- Special Requests -->
            <div class="space-y-2">
                <Label for="special_requests">Special Requests</Label>
                <textarea
                    id="special_requests"
                    name="special_requests"
                    v-model="signup.special_requests"
                    rows="4"
                    placeholder="Let us know ..."
                    :class="textareaClasses"
                />
                <InputError :message="errors.special_requests" />
            </div>

            <!-- How did you hear about us? -->
            <div class="space-y-2">
                <Label for="referral">How did you hear about us?</Label>
                <select
                    id="referral"
                    name="referral"
                    v-model="signup.referral"
                    :class="selectClasses"
                >
                    <option value="" disabled>Select</option>
                    <option value="web">Web Search</option>
                    <option value="Social">Social</option>
                    <option value="Mail Card">Mail Card</option>
                    <option value="referral">Referral</option>
                    <option value="Adding Property">Adding Property</option>
                    <option value="Thumbtack">Thumbtack</option>
                </select>
                <InputError :message="errors.referral" />

                <div
                    v-if="signup.referral === 'web'"
                    class="mt-3 space-y-2 border-l-2 border-mm-green/30 pl-6"
                >
                    <Label for="referral_2">Which search engine?</Label>
                    <select
                        id="referral_2"
                        name="referral_2"
                        v-model="signup.referral_2"
                        :class="selectClasses"
                    >
                        <option value="" disabled>Select</option>
                        <option value="google">Google</option>
                        <option value="yahoo">Yahoo</option>
                        <option value="bing">Bing</option>
                        <option value="other">Other</option>
                    </select>
                    <InputError :message="errors.referral_2" />
                </div>

                <div
                    v-if="signup.referral === 'Social'"
                    class="mt-3 space-y-2 border-l-2 border-mm-green/30 pl-6"
                >
                    <Label for="referral_2">Which platform?</Label>
                    <select
                        id="referral_2"
                        name="referral_2"
                        v-model="signup.referral_2"
                        :class="selectClasses"
                    >
                        <option value="" disabled>Select</option>
                        <option value="facebook">Facebook</option>
                        <option value="instagram">Instagram</option>
                        <option value="next_door">Nextdoor</option>
                    </select>
                    <InputError :message="errors.referral_2" />
                </div>

                <div
                    v-if="signup.referral === 'referral'"
                    class="mt-3 space-y-2 border-l-2 border-mm-green/30 pl-6"
                >
                    <Label for="referral_2">Referred by?</Label>
                    <select
                        id="referral_2"
                        name="referral_2"
                        v-model="signup.referral_2"
                        :class="selectClasses"
                    >
                        <option value="" disabled>Select</option>
                        <option value="friend">Friend</option>
                        <option value="neighbor">Neighbor</option>
                        <option value="law_tech_dallas">LawnTech Dallas</option>
                        <option value="go_mow">GoMow</option>
                        <option value="law_co">LawnCo</option>
                    </select>
                    <InputError :message="errors.referral_2" />
                </div>
            </div>
        </div>

        <!-- Billing Details -->
        <h2 class="pt-4 text-xl font-bold text-mm-dark">Billing Details</h2>

        <input type="hidden" name="card_number" :value="signup.card_number" />
        <input
            type="hidden"
            name="card_billing_zip"
            :value="
                signup.billing_same_as_service
                    ? signup.card_billing_zip
                    : signup.billing_zip
            "
        />
        <input type="hidden" name="card_type" :value="cardType" />
        <input type="hidden" name="card_cvv" :value="signup.card_cvv" />
        <input
            type="hidden"
            name="billing_same_as_service"
            :value="signup.billing_same_as_service ? '1' : '0'"
        />
        <input
            v-if="!signup.billing_same_as_service"
            type="hidden"
            name="billing_address"
            :value="signup.billing_address"
        />
        <input
            v-if="!signup.billing_same_as_service"
            type="hidden"
            name="billing_city"
            :value="signup.billing_city"
        />
        <input
            v-if="!signup.billing_same_as_service"
            type="hidden"
            name="billing_state"
            :value="signup.billing_state"
        />
        <input
            v-if="!signup.billing_same_as_service"
            type="hidden"
            name="billing_zip"
            :value="signup.billing_zip"
        />

        <div class="space-y-2">
            <Label>Billing address same as service address?</Label>
            <div class="space-y-1">
                <label class="flex cursor-pointer items-center gap-3">
                    <input
                        type="radio"
                        name="billing_same_as_service_toggle"
                        value="yes"
                        :checked="signup.billing_same_as_service"
                        class="peer sr-only"
                        @change="signup.billing_same_as_service = true"
                    />
                    <span
                        class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
                    >
                        <svg
                            v-if="signup.billing_same_as_service"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="3"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="size-3 text-white"
                        >
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                    </span>
                    <span class="text-sm">Yes</span>
                </label>
                <label class="flex cursor-pointer items-center gap-3">
                    <input
                        type="radio"
                        name="billing_same_as_service_toggle"
                        value="no"
                        :checked="!signup.billing_same_as_service"
                        class="peer sr-only"
                        @change="signup.billing_same_as_service = false"
                    />
                    <span
                        class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
                    >
                        <svg
                            v-if="!signup.billing_same_as_service"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="3"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="size-3 text-white"
                        >
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                    </span>
                    <span class="text-sm">No</span>
                </label>
            </div>
        </div>

        <div v-if="!signup.billing_same_as_service" class="space-y-4">
            <div class="space-y-2">
                <Label for="billing_address"
                    >Billing Address <span class="text-mm-green">*</span></Label
                >
                <input
                    id="billing_address"
                    v-model="signup.billing_address"
                    type="text"
                    required
                    placeholder="123 Main St"
                    :class="inputClasses"
                />
                <InputError :message="errors.billing_address" />
            </div>
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div class="space-y-2">
                    <Label for="billing_city"
                        >Billing City
                        <span class="text-mm-green">*</span></Label
                    >
                    <input
                        id="billing_city"
                        v-model="signup.billing_city"
                        type="text"
                        required
                        placeholder="Dallas"
                        :class="inputClasses"
                    />
                    <InputError :message="errors.billing_city" />
                </div>
                <div class="space-y-2">
                    <Label for="billing_state"
                        >Billing State
                        <span class="text-mm-green">*</span></Label
                    >
                    <input
                        id="billing_state"
                        v-model="signup.billing_state"
                        type="text"
                        required
                        maxlength="2"
                        placeholder="TX"
                        :class="inputClasses"
                        style="text-transform: uppercase"
                        @input="
                            signup.billing_state = (
                                $event.target as HTMLInputElement
                            ).value.toUpperCase()
                        "
                    />
                    <InputError :message="errors.billing_state" />
                </div>
                <div class="space-y-2">
                    <Label for="billing_zip"
                        >Billing Zip <span class="text-mm-green">*</span></Label
                    >
                    <input
                        id="billing_zip"
                        v-maska="{ mask: '#####', eager: true }"
                        v-model="signup.billing_zip"
                        type="text"
                        required
                        inputmode="numeric"
                        maxlength="5"
                        placeholder="75201"
                        :class="inputClasses"
                    />
                    <InputError :message="errors.billing_zip" />
                </div>
            </div>
        </div>

        <div class="space-y-6">
            <div class="space-y-2">
                <Label for="card_name"
                    >Name on card <span class="text-mm-green">*</span></Label
                >
                <input
                    id="card_name"
                    name="card_name"
                    v-model="signup.card_name"
                    type="text"
                    required
                    placeholder="Olivia Rhye"
                    :class="inputClasses"
                />
                <InputError :message="errors.card_name" />
            </div>
            <div class="space-y-2">
                <Label for="card_number"
                    >Card number <span class="text-mm-green">*</span></Label
                >
                <div class="relative">
                    <input
                        id="card_number"
                        v-maska="{
                            mask: cardNumberMask,
                            eager: true,
                            onMaska: onCardNumberMaska,
                        }"
                        type="text"
                        required
                        inputmode="numeric"
                        placeholder="1234 1234 1234 1234"
                        :class="[inputClasses, 'pl-12']"
                    />
                    <div
                        class="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
                    >
                        <!-- Visa -->
                        <svg
                            v-if="cardBrand === 'visa'"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 48 32"
                            class="h-6 w-8"
                        >
                            <rect
                                width="48"
                                height="32"
                                rx="4"
                                fill="#1A1F71"
                            />
                            <path
                                d="M19.5 21h-2.7l1.7-10.5h2.7L19.5 21zm11.2-10.2c-.5-.2-1.4-.4-2.4-.4-2.7 0-4.5 1.4-4.5 3.4 0 1.5 1.4 2.3 2.4 2.8 1 .5 1.4.8 1.4 1.3 0 .7-.8 1-1.6 1-1.1 0-1.6-.2-2.5-.5l-.3-.2-.4 2.2c.6.3 1.8.5 3 .5 2.8 0 4.7-1.4 4.7-3.5 0-1.2-.7-2-2.3-2.8-.9-.5-1.5-.8-1.5-1.3 0-.4.5-.9 1.5-.9.9 0 1.5.2 2 .4l.2.1.3-2.1zm6.8-.3h-2.1c-.6 0-1.1.2-1.4.8L30 21h2.8l.6-1.5h3.5l.3 1.5H40l-2.3-10.5h-2.2zm-2.3 6.8l1.1-3 .3-.8.2.7.6 3.1h-2.2zM16.3 10.5L13.6 18l-.3-1.4c-.5-1.7-2-3.5-3.8-4.4l2.4 8.8h2.9l4.3-10.5h-2.8z"
                                fill="#fff"
                            />
                            <path
                                d="M11.5 10.5H7.1l0 .2c3.4.9 5.6 2.9 6.5 5.4l-.9-4.7c-.2-.7-.7-.9-1.2-.9z"
                                fill="#F9A533"
                            />
                        </svg>
                        <!-- Mastercard -->
                        <svg
                            v-else-if="cardBrand === 'mastercard'"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 48 32"
                            class="h-6 w-8"
                        >
                            <rect
                                width="48"
                                height="32"
                                rx="4"
                                fill="#252525"
                            />
                            <circle cx="19" cy="16" r="8" fill="#EB001B" />
                            <circle cx="29" cy="16" r="8" fill="#F79E1B" />
                            <path
                                d="M24 9.8a8 8 0 0 1 0 12.4 8 8 0 0 1 0-12.4z"
                                fill="#FF5F00"
                            />
                        </svg>
                        <!-- Amex -->
                        <svg
                            v-else-if="cardBrand === 'american-express'"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 48 32"
                            class="h-6 w-8"
                        >
                            <rect
                                width="48"
                                height="32"
                                rx="4"
                                fill="#2E77BC"
                            />
                            <text
                                x="24"
                                y="19"
                                text-anchor="middle"
                                fill="#fff"
                                font-size="8"
                                font-weight="bold"
                                font-family="sans-serif"
                            >
                                AMEX
                            </text>
                        </svg>
                        <!-- Discover -->
                        <svg
                            v-else-if="cardBrand === 'discover'"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 48 32"
                            class="h-6 w-8"
                        >
                            <rect
                                width="48"
                                height="32"
                                rx="4"
                                fill="#fff"
                                stroke="#E5E7EB"
                            />
                            <circle cx="28" cy="16" r="7" fill="#F76B1C" />
                            <text
                                x="13"
                                y="19"
                                fill="#111"
                                font-size="6"
                                font-weight="bold"
                                font-family="sans-serif"
                            >
                                DISC
                            </text>
                        </svg>
                        <!-- Default card icon -->
                        <svg
                            v-else
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.5"
                            class="h-6 w-6 text-gray-400"
                        >
                            <rect x="1" y="4" width="22" height="16" rx="2" />
                            <line x1="1" y1="10" x2="23" y2="10" />
                        </svg>
                    </div>
                </div>
                <InputError
                    :message="errors.card_number || clientErrors.card_number"
                />
            </div>
            <div
                :class="[
                    'grid grid-cols-1 gap-4',
                    signup.billing_same_as_service
                        ? 'sm:grid-cols-3'
                        : 'sm:grid-cols-2',
                ]"
            >
                <div class="space-y-2">
                    <Label for="card_expiration"
                        >Expiration Date
                        <span class="text-mm-green">*</span></Label
                    >
                    <input
                        id="card_expiration"
                        name="card_expiration"
                        v-maska="{
                            mask: '##/##',
                            eager: true,
                            onMaska: onCardExpirationMaska,
                        }"
                        type="text"
                        required
                        inputmode="numeric"
                        placeholder="01/25"
                        :class="inputClasses"
                    />
                    <InputError
                        :message="
                            errors.card_expiration ||
                            clientErrors.card_expiration
                        "
                    />
                </div>
                <div class="space-y-2">
                    <Label for="card_cvv"
                        >{{ cvvLabel }}
                        <span class="text-mm-green">*</span></Label
                    >
                    <input
                        id="card_cvv"
                        v-maska="{ mask: cvvMask, onMaska: onCardCvvMaska }"
                        type="password"
                        required
                        inputmode="numeric"
                        placeholder="&bull;&bull;&bull;"
                        :class="inputClasses"
                    />
                    <InputError
                        :message="errors.card_cvv || clientErrors.card_cvv"
                    />
                </div>
                <div v-if="signup.billing_same_as_service" class="space-y-2">
                    <Label for="card_billing_zip"
                        >Billing Zip <span class="text-mm-green">*</span></Label
                    >
                    <input
                        id="card_billing_zip"
                        v-maska="{ mask: '#####', eager: true }"
                        v-model="signup.card_billing_zip"
                        type="text"
                        required
                        inputmode="numeric"
                        maxlength="5"
                        placeholder="75201"
                        :class="inputClasses"
                    />
                    <InputError :message="errors.card_billing_zip" />
                </div>
            </div>
        </div>

        <div v-if="recaptchaSiteKey" class="flex flex-col items-center gap-2">
            <div id="recaptcha-checkbox" />
            <InputError
                :message="
                    errors.recaptcha_token || clientErrors.recaptcha_token
                "
            />
        </div>

        <!-- Actions -->
        <div class="flex items-center justify-center gap-4 pt-4">
            <button
                type="button"
                class="cursor-pointer rounded-md border-2 border-gray-300 px-[36.5px] py-[14.6px] text-[14px] font-bold tracking-[1.6px] text-mm-dark uppercase transition-colors hover:border-gray-400"
                @click="$emit('back')"
            >
                Back
            </button>
            <Button
                type="submit"
                :disabled="processing"
                class="h-auto rounded-md bg-mm-green px-[36.5px] py-[14.6px] text-[14px] font-bold tracking-[1.6px] text-white uppercase hover:bg-mm-green-dark"
            >
                <Spinner v-if="processing" />
                Submit
            </Button>
        </div>
    </Form>
</template>
